/**
 * Lecturas de reportes — contratos y formato (PROMPT_N°056).
 *
 * Los reportes del N°054 y el kardex del N°053 muestran números correctos
 * pero mudos. El cliente no es contador: necesita que el reporte le diga qué
 * está viendo y qué merece atención.
 *
 * Las frases se arman con **reglas fijas sobre los datos ya calculados**, sin
 * IA generativa. No es una limitación: una frase que afirma "3 productos no
 * cuadran" tiene que ser verdadera, reproducible y testeable, y tiene que
 * poder enlazar a las 3 filas exactas. Un texto generado no da ninguna de las
 * tres cosas.
 *
 * El correo diario (N°057) va a usar estas mismas lecturas como cuerpo del
 * mensaje, así que el texto tiene que sostenerse sin la tabla al lado.
 */
/** Orden de importancia: lo que hay que mirar primero va primero. */
export const ORDEN_NIVEL = {
    alerta: 0,
    atencion: 1,
    ok: 2,
    info: 3,
};
export const ETIQUETA_DESTACADO = {
    top_valor: 'Top valor',
    bajo_minimo: 'Bajo mínimo',
    no_cuadra: 'No cuadra',
    sin_costo: 'Sin costo',
    vencido: 'Vencido',
    por_vencer: 'Por vencer',
    sin_fecha: 'Sin fecha',
    mas_movido: 'Más movido',
    ajuste: 'Ajuste manual',
};
export const NIVEL_DESTACADO = {
    top_valor: 'info',
    bajo_minimo: 'alerta',
    no_cuadra: 'alerta',
    sin_costo: 'atencion',
    vencido: 'alerta',
    por_vencer: 'atencion',
    sin_fecha: 'info',
    mas_movido: 'info',
    ajuste: 'atencion',
};
/** Tope de frases. Más que esto deja de leerse y vuelve a ser un listado. */
export const MAX_FRASES = 6;
/**
 * Deja la lectura lista para mostrar: ordena por nivel, corta en 6 y, si no
 * quedó nada que decir, pone una sola frase en verde.
 *
 * El orden es estable dentro de cada nivel (`sort` lo es en ES2019+), así que
 * el orden en que cada módulo agrega sus frases se respeta entre iguales.
 */
export const armarLectura = (frases, destacados = [], sinNovedad = 'Sin novedades en este reporte.') => {
    const utiles = frases.filter((f) => f.texto.trim().length > 0);
    if (utiles.length === 0) {
        return { frases: [{ nivel: 'ok', texto: sinNovedad }], destacados };
    }
    const ordenadas = [...utiles].sort((a, b) => ORDEN_NIVEL[a.nivel] - ORDEN_NIVEL[b.nivel]);
    return { frases: ordenadas.slice(0, MAX_FRASES), destacados };
};
// ---------------------------------------------------------------------------
// Formato
// ---------------------------------------------------------------------------
/**
 * Miles con punto y decimales con coma, como se escribe en Chile. Se usa
 * `es-CL` y no un formateo a mano porque las frases viajan al correo y a un
 * Excel, donde un número mal escrito se nota más que en pantalla.
 */
export const fmtNumero = (n, decimales = 0) => Number(n).toLocaleString('es-CL', {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
});
/** Pesos sin decimales: nadie lee centavos en un informe de bodega. */
export const fmtDinero = (n) => `$${fmtNumero(Math.round(n))}`;
/** Porcentajes sin decimales, por pedido explícito. */
export const fmtPorcentaje = (parte, total) => total > 0 ? `${Math.round((parte / total) * 100)}%` : '0%';
/** Cantidad con su unidad; la unidad puede venir vacía. */
export const fmtCantidad = (n, unidad) => {
    // Hasta 3 decimales, pero sin arrastrar ceros: 100 g, no 100,000 g.
    const texto = Number(n).toLocaleString('es-CL', {
        maximumFractionDigits: 3,
    });
    return unidad ? `${texto} ${unidad}` : texto;
};
/** "producto" / "productos", sin el paréntesis horrible. */
export const plural = (n, singular, plural_) => n === 1 ? singular : (plural_ ?? `${singular}s`);
/**
 * Enumera nombres en una frase, cortando en `max` y diciendo cuántos quedaron
 * fuera. Sin esto, una frase con 40 productos es ilegible.
 */
export const listar = (nombres, max = 5) => {
    if (nombres.length === 0)
        return '';
    if (nombres.length <= max)
        return nombres.join(', ');
    const restantes = nombres.length - max;
    return `${nombres.slice(0, max).join(', ')} y ${restantes} ${plural(restantes, 'más', 'más')}`;
};
/**
 * El texto que ve el usuario al abrir "¿Qué es este reporte?".
 *
 * Vive acá y no dentro de los componentes para que se pueda ajustar la
 * redacción sin tocar React, y para que el correo del N°057 pueda usar el
 * mismo texto.
 */
export const AYUDA_REPORTES = {
    existencias: {
        titulo: 'Existencias valorizadas',
        paraQue: 'Saber qué hay en bodega hoy y cuánto vale.',
        como: {
            etiqueta: 'Cómo se calcula',
            texto: 'Cada producto se valoriza por sus lotes, al precio al que efectivamente se compró cada uno; si un lote entró sin precio, se usa el precio de referencia del catálogo y se indica cuántos son. Los productos sin precio no suman.',
        },
        queMirar: 'Los productos que concentran el valor (ahí está la plata), los que están bajo su mínimo (hay que comprar) y los que llevan semanas sin moverse (plata parada).',
    },
    conciliacion: {
        titulo: 'Conciliación del período',
        paraQue: 'Comprobar que todo lo que entró y salió quedó registrado.',
        como: {
            etiqueta: 'Cómo se calcula',
            texto: 'Para cada producto, saldo al inicio del período + entradas − salidas ± ajustes, y se compara con el saldo que tiene el sistema.',
        },
        queMirar: 'Una diferencia distinta de cero significa que el stock cambió sin un movimiento que lo explique; hay que revisar ese producto en el kardex. Si todo cuadra, el inventario del período es confiable.',
    },
    lotes: {
        titulo: 'Lotes y vencimientos',
        paraQue: 'Rotar bien y no botar plata.',
        como: {
            etiqueta: 'Cómo se calcula',
            texto: 'Cada entrada crea un lote con su fecha de ingreso, vencimiento y costo; las salidas consumen primero lo que vence antes.',
        },
        queMirar: 'Lo que vence en 7 y 30 días (consumirlo o devolverlo), los lotes vencidos (registrar merma) y los lotes sin fecha (cargarla para que el semáforo funcione).',
    },
    compras: {
        titulo: 'Compras del período',
        paraQue: 'Saber cuánto se compró, a quién y qué entró con cada factura.',
        como: {
            etiqueta: 'Cómo se calcula',
            texto: 'Suma de las facturas registradas en el rango, con su neto e IVA; las recepciones sin factura se muestran aparte porque no son gasto documentado.',
        },
        queMirar: 'Concentración por proveedor, facturas grandes, y productos comprados sobre su precio de referencia.',
    },
    conteo: {
        titulo: 'Planilla de conteo físico',
        paraQue: 'Contar la bodega a mano y comparar con el sistema.',
        como: {
            etiqueta: 'Cómo se usa',
            texto: 'Imprimir sin el stock del sistema, contar, anotar y después comparar; contar "a ciegas" evita que el número de la pantalla influya en el conteo.',
        },
        queMirar: 'Las diferencias; cada una se corrige con una entrada o salida con motivo "inventario físico", nunca cambiando el número a mano.',
    },
    kardex: {
        titulo: 'Movimientos de bodega (kardex)',
        paraQue: 'Ver todo lo que entró y salió, cuándo, cuánto, de qué lote y quién lo registró.',
        como: {
            etiqueta: 'Cómo funciona',
            texto: 'Cada entrada, salida, reverso y ajuste queda como una fila que no se borra; un error se corrige con un reverso, que queda a la vista.',
        },
        queMirar: 'Reversos frecuentes (alguien está registrando mal), ajustes manuales (siempre deben tener explicación) y productos que se mueven mucho.',
    },
};
